/**
 * Character data migration framework.
 * Handles upgrading character data from older schema versions to the current version.
 */
import type { CharacterExportData, RuntimeState } from './characterExport';
import type { CharacterDraft } from '../stores/characterCreationStore';

/**
 * Current schema version.
 * This should match SCHEMA_VERSION in characterExport.ts
 */
export const CURRENT_SCHEMA_VERSION = '1.0';

/**
 * Ordered list of all schema versions from oldest to newest.
 * Used to determine which migrations need to run.
 */
export const SCHEMA_VERSION_HISTORY = ['1.0'] as const;

/**
 * Result of a migration operation.
 */
export interface MigrationResult {
  /** The migrated data */
  data: CharacterExportData;
  /** Warnings generated during migration */
  warnings: string[];
  /** Whether any migrations were applied */
  migrated: boolean;
  /** The original version before migration */
  fromVersion: string;
  /** The final version after migration */
  toVersion: string;
}

/**
 * Migration function signature.
 * Takes data at version N and returns data at version N+1.
 */
export type MigrationFn = (data: unknown) => {
  data: unknown;
  warnings: string[];
};

/**
 * Registry of migration functions.
 * Key is the source version, function migrates to the next version.
 *
 * Example when adding a migration from 1.0 to 1.1:
 * ```typescript
 * migrations['1.0'] = (data) => {
 *   const d = data as CharacterExportDataV1_0;
 *   // Transform data...
 *   return {
 *     data: { ...d, schemaVersion: '1.1', newField: defaultValue },
 *     warnings: ['Migrated from 1.0 to 1.1: newField set to default'],
 *   };
 * };
 * ```
 */
export const migrations: Record<string, MigrationFn> = {
  // Future migrations will be added here.
  // Example:
  // '1.0': migrateFrom1_0To1_1,
};

/**
 * Get the index of a version in the version history.
 * Returns -1 if version is not found.
 */
function getVersionIndex(version: string): number {
  return SCHEMA_VERSION_HISTORY.indexOf(version as typeof SCHEMA_VERSION_HISTORY[number]);
}

/**
 * Compare two version strings.
 * Returns:
 *  - negative if a < b
 *  - 0 if a === b
 *  - positive if a > b
 */
export function compareVersions(a: string, b: string): number {
  const indexA = getVersionIndex(a);
  const indexB = getVersionIndex(b);

  // Unknown versions are treated as older than known versions
  if (indexA === -1 && indexB === -1) return 0;
  if (indexA === -1) return -1;
  if (indexB === -1) return 1;

  return indexA - indexB;
}

/**
 * Check if a version needs migration.
 */
export function needsMigration(version: string): boolean {
  return compareVersions(version, CURRENT_SCHEMA_VERSION) < 0;
}

/**
 * Migrate data from an older schema version to the current version.
 * Applies migrations sequentially from the source version to the current version.
 *
 * @param data - The character export data to migrate
 * @param fromVersion - The schema version of the input data
 * @returns The migrated data with any warnings
 */
export function migrateToLatest(
  data: unknown,
  fromVersion: string
): MigrationResult {
  const allWarnings: string[] = [];
  let currentData = data;
  let currentVersion = fromVersion;

  // If version is unknown or already current, return as-is
  const fromIndex = getVersionIndex(fromVersion);
  if (fromIndex === -1) {
    // Unknown version - can't migrate, but warn
    return {
      data: ensureValidExportData(currentData),
      warnings: [
        `Unknown schema version "${fromVersion}". Data may not import correctly.`,
      ],
      migrated: false,
      fromVersion,
      toVersion: fromVersion,
    };
  }

  // No migration needed if already at current version
  if (!needsMigration(fromVersion)) {
    return {
      data: ensureValidExportData(currentData),
      warnings: [],
      migrated: false,
      fromVersion,
      toVersion: currentVersion,
    };
  }

  // Apply migrations sequentially
  const currentIndex = getVersionIndex(CURRENT_SCHEMA_VERSION);
  for (let i = fromIndex; i < currentIndex; i++) {
    const sourceVersion = SCHEMA_VERSION_HISTORY[i];
    const migration = migrations[sourceVersion];

    if (migration) {
      const result = migration(currentData);
      currentData = result.data;
      allWarnings.push(...result.warnings);
      currentVersion = SCHEMA_VERSION_HISTORY[i + 1];
    } else {
      // No migration defined for this version - skip but warn
      allWarnings.push(
        `No migration defined from version ${sourceVersion}. Some data may not migrate correctly.`
      );
    }
  }

  return {
    data: ensureValidExportData(currentData),
    warnings: allWarnings,
    migrated: true,
    fromVersion,
    toVersion: currentVersion,
  };
}

/**
 * Ensure data conforms to CharacterExportData structure.
 * Fills in missing required fields with defaults.
 */
function ensureValidExportData(data: unknown): CharacterExportData {
  if (!data || typeof data !== 'object') {
    throw new Error('Invalid data: expected object');
  }

  const d = data as Record<string, unknown>;

  return {
    schemaVersion: typeof d.schemaVersion === 'string' ? d.schemaVersion : CURRENT_SCHEMA_VERSION,
    exportedAt: typeof d.exportedAt === 'string' ? d.exportedAt : new Date().toISOString(),
    draft: d.draft as CharacterDraft,
    runtimeState: d.runtimeState as RuntimeState | undefined,
  };
}

/**
 * Get a list of versions between two versions (exclusive of start, inclusive of end).
 * Used for logging which migrations will run.
 */
export function getVersionsBetween(fromVersion: string, toVersion: string): string[] {
  const fromIndex = getVersionIndex(fromVersion);
  const toIndex = getVersionIndex(toVersion);

  if (fromIndex === -1 || toIndex === -1 || fromIndex >= toIndex) {
    return [];
  }

  return SCHEMA_VERSION_HISTORY.slice(fromIndex + 1, toIndex + 1) as string[];
}
